// Side-effecting bootstrap module. Mirrors the crypto-polyfill pattern:
// imported at the top of __root.tsx so it runs at module-eval time — before
// any React render or error boundary activation.
//
// SSR-guarded: Sentry browser SDK must not run on the server (Workers) side.
// Worker-side Sentry is handled by @sentry/cloudflare in add-queue-handler.mjs.

import * as Sentry from '@sentry/react'
import { sentryConfig } from '~/client/common/sentry'
import { useAuthStore } from '~/client/common/stores/auth'

// Type label for a rejected value (typeof loses null/undefined). Only the TYPE is exposed —
// never the payload, which could carry PII into the unscrubbed Sentry issue title.
function classifyRejection(reason: unknown): string {
  if (reason === undefined) return 'undefined'
  if (reason === null) return 'null'
  return typeof reason
}

// `import.meta.env.DEV` is true only under the Vite dev server, never in a built deploy.
// Skipping init in dev keeps local Fast Refresh / HMR artifacts (transient ReferenceErrors,
// @react-refresh noise) out of the shared Sentry project — they never occur in production.
if (typeof window !== 'undefined' && !import.meta.env.DEV) {
  // ── Initialize Sentry synchronously at module load ────────────────────────
  // This guarantees captureException calls from DefaultCatchBoundary (which
  // renders as the route errorComponent) always hit an initialized SDK, even
  // on errors that occur during or immediately after hydration.
  Sentry.init(sentryConfig)

  // ── Attach authenticated user identity to every subsequent event ──────────
  // Rides the existing Zustand auth-store subscriber pattern (same approach
  // as the PostHog super-property subscription in auth.ts:564-574).
  // INVARIANT: always the admin identity — never viewAsUser.
  useAuthStore.subscribe((state, prevState) => {
    const userChanged = state.user !== prevState.user || state.userDetail !== prevState.userDetail
    const impersonationChanged = state.viewAsUser !== prevState.viewAsUser

    if (userChanged) {
      if (state.user && state.userDetail) {
        Sentry.setUser({ id: state.user.id, email: state.userDetail.email ?? undefined })
      } else if (state.user && !state.userDetail) {
        // Partial auth state: user is known but userDetail is still loading (rehydration).
        // Set id-only identity so no stale prior identity remains attached.
        Sentry.setUser({ id: state.user.id })
      } else if (!state.user) {
        Sentry.setUser(null)
      }
    }

    if (impersonationChanged) {
      Sentry.setTag('impersonating', state.viewAsUser?.id ?? null)
    }
  })

  // Own unhandled-rejection reporting (the SDK's default is disabled in sentryConfig).
  // Synthesize an Error so non-Error rejections get a real stack + page context instead of
  // the opaque "Non-Error promise rejection ... value: undefined".
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason: unknown = event.reason
      // SDK handler is off, so set the unhandled mechanism ourselves or these count as `handled`.
      const mechanism = { type: 'onunhandledrejection', handled: false }
      // Real Errors already carry a useful stack — report them as-is.
      if (reason instanceof Error) {
        Sentry.captureException(reason, { mechanism })
        return
      }
      // Type only in the message — never the payload (PII safety).
      const synthetic = new Error(`Unhandled promise rejection (${classifyRejection(reason)})`)
      synthetic.name = 'UnhandledRejection'
      Sentry.captureException(synthetic, {
        mechanism,
        captureContext: (scope) => {
          scope.setTag('rejection_kind', 'non-error')
          scope.setExtras({
            rejectionType: classifyRejection(reason),
            pageUrl: window.location.href,
          })
          return scope
        },
      })
    } catch {
      // Never let instrumentation throw inside the global handler.
    }
  })
}
